import { uniq, without } from 'lodash'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocalStorage } from 'react-use'
import { addFavoritesByUser, favoritesByUser } from 'shared/api/favorites'
// import { favoritesByUser } from 'shared/api/favorites'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import useSameObjectRef from 'shared/hooks/use-same-ref'
import { LoggedInUser } from 'shared/types/user'
import UserContext from './user'

type Props = {
    user: LoggedInUser
    isLoggedIn: boolean
}

const UserManager: FC<Props> = ({ isLoggedIn, user, children }) => {
    const [userFavorites, setUserFavorites] = useState<(string | number)[]>([])
    const [hasFetchedFavorites, setHasFetchedFavorites] = useState(false)
    const [isFetchingFavorites, setIsFetchingFavorites] = useState(false)
    const [localFavorites, setLocalFavorites] = useLocalStorage<(string | number)[]>('local-favorites', [])
    const hasSyncedLocalFavorites = useRef(false)
    useAsyncEffect(async () => {
        if (isLoggedIn && !hasFetchedFavorites && !isFetchingFavorites) {
            setIsFetchingFavorites(true)
            // fetch all the favorites by id
            const gifs = await favoritesByUser(user, 0, 5000, ['id'])
            setHasFetchedFavorites(true)
            setIsFetchingFavorites(false)
            // if these have changed while the above loads, they'll be ignored
            setUserFavorites(uniq([...localFavorites, ...userFavorites, ...gifs.data.map(g => g.id)]))
        }
    }, [user, isLoggedIn, userFavorites])
    useEffect(() => {
        if (!user.isFetching && !user.loggedIn) {
            setHasFetchedFavorites(true)
        }
    }, [user])
    const addFavorite = useCallback(
        id => {
            if (isLoggedIn) {
                if (userFavorites.indexOf(id) === -1) {
                    setUserFavorites([id, ...userFavorites])
                }
            } else {
                if (localFavorites.indexOf(id) === -1) {
                    setLocalFavorites([id, ...localFavorites])
                }
            }
        },
        [userFavorites, localFavorites]
    )
    const removeFavorite = useCallback(
        id => {
            if (isLoggedIn) {
                setUserFavorites(without(userFavorites, id))
            } else {
                setLocalFavorites(without(localFavorites, id))
            }
        },
        [userFavorites]
    )
    const userValue = useSameObjectRef({
        user,
        isLoggedIn,
        favorites: isLoggedIn ? userFavorites : localFavorites,
        localFavorites,
        addFavorite,
        removeFavorite,
        hasFetchedFavorites,
    })
    // if a user wasn't logged in,
    // and added favorites, they will have local favorites
    useAsyncEffect(async () => {
        const failedSync: (string | number)[] = []
        if (!hasSyncedLocalFavorites.current && localFavorites.length > 0 && isLoggedIn && user?.userToken) {
            hasSyncedLocalFavorites.current = true
            await Promise.all(
                localFavorites.map(async id => {
                    // shoot off a bunch of requests for now.
                    // multiple gif ids will be supported eventually
                    // see https://giphypedia.atlassian.net/browse/API-2807
                    try {
                        await addFavoritesByUser(user.userToken, id)
                    } catch (_) {
                        failedSync.push(id)
                    }
                })
            )
            // include in our favorites state
            setUserFavorites(uniq([...userFavorites, ...localFavorites]))
            // remove
            setLocalFavorites(failedSync)
        }
    }, [localFavorites, userFavorites, isLoggedIn, user?.userToken])
    return <UserContext.Provider value={userValue}>{children}</UserContext.Provider>
}

export default UserManager
